import { getTiposSolicitud } from "@/services/request-policies.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { motivesSchema, MotivoDetail } from "../schema/motives.schema";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { isEmpty } from "@/utils/query-string";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { Card, Flex, Grid, GridItem, Text } from "@chakra-ui/react";
import { AppColors } from "@/styles/colors";
import { SelectComponent } from "@/components/select/Select";
import { TextArea } from "@/components/text-area/TextArea";
import { getDetalleMotivo } from "@/services/motives.service";
import { handleUpdateMotiveHandler } from "../schema/handler-functions";
import { onlyAlphanumericAndSpaces } from "@/utils/reg-expressions";
import { useRoles } from "@/hooks/useRoles";

export const MotiveUpdatePage = () => {
  const { id } = useParams();
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [loadingTiposSolicitud, setLoadingTiposSolicitud] =
    useState<boolean>(false);

  const [loadingMotive, setLoadingMotive] = useState<boolean>(false);

  const [motive, setMotive] = useState<MotivoDetail | null | undefined>();

  const [loadingUpdateMotive, setLoadingUpdateMotive] =
    useState<boolean>(false);

  const [tiposSolicitud, setTiposSolicitud] = useState<
    { id: string; descripcion: string }[]
  >([]);

  const {
    getValues,
    setValue,
    register,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(motivesSchema),
  });

  const fetchMotivo = async (id: number) => {
    setLoadingMotive(true);

    const response = await getDetalleMotivo({ idMotivo: id });

    setMotive(response);

    setLoadingMotive(false);
  };

  const fetchTiposSolicitud = async () => {
    setLoadingTiposSolicitud(true);

    const response = await getTiposSolicitud();

    setTiposSolicitud(response);

    setLoadingTiposSolicitud(false);
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (isActive && id) {
      fetchTiposSolicitud();
      fetchMotivo(Number(id));
    }
  }, [isActive, id]);

  const { fetchPermissions } = useRoles();

  const handleCreateMotive = async () => {
    handleUpdateMotiveHandler({
      getValues,
      navigate,
      setLoadingUpdateMotive,
      id: Number(id),
      fetchPermissions
    });
  };

  const handleCancel = () => {
    navigate(AppRoutes.MOTIVES);
  };

  const watchedValues = useWatch({
    control,
  });

  useEffect(() => {
    if (motive) {
      setValue("descripcion", motive?.descripcion);
      setValue(
        "tipoSolicitud",
        motive?.tiposSolicitud?.map((el) => el?.id),
      );
      setValue("tipoMotivo", motive?.tiposMotivo);
    }
  }, [motive]);

  const hasNoFilledValues = useMemo(() => {
    const { tipoSolicitud, tipoMotivo, descripcion } = watchedValues || {};

    return (
      isEmpty(tipoSolicitud) || isEmpty(tipoMotivo) || isEmpty(descripcion)
    );
  }, [watchedValues]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Editar motivo"
        addButtonLabel="Confirmar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateMotive}
        addButtonType="submit"
        disableAdd={
          loadingTiposSolicitud ||
          hasNoFilledValues ||
          loadingUpdateMotive ||
          loadingMotive
        }
        loading={loadingUpdateMotive || loadingMotive}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                multiple
                items={[
                  {
                    label: "Rechazo",
                    value: "Rechazo",
                  },
                  {
                    label: "Requiere información",
                    value: "Requiere información",
                  },
                ]}
                control={control}
                name="tipoMotivo"
                labelText="Tipo"
                clearable
                closeOnSelect
                required
                disabled={loadingUpdateMotive || loadingMotive}
                placeholder="Seleccionar clase"
              />
            </GridItem>
            <GridItem paddingX="16px" paddingY="8px">
              <SelectComponent
                minHeight="40px"
                labelText="Tipo de solicitud"
                control={control}
                name="tipoSolicitud"
                multiple
                placeholder="Buscar..."
                items={tiposSolicitud.map((tipo) => ({
                  label: tipo?.descripcion,
                  value: tipo?.id,
                }))}
                required
                disabled={
                  loadingUpdateMotive || loadingTiposSolicitud || loadingMotive
                }
                loading={loadingTiposSolicitud}
                closeOnSelect={false}
              />
            </GridItem>
          </Grid>
          <Flex gap={"50px"}></Flex>
        </Card.Body>
      </Card.Root>
      <Flex flexDir="column" gap="12px">
        <Text fontWeight={700} fontSize="20px">
          Descripción
        </Text>
        <TextArea
          errors={errors}
          inputName="descripcion"
          maxLength={30}
          register={register}
          placeholder="Ingresar una descripción del motivo"
          helperText="Máximo 30 caracteres"
          disabled={loadingUpdateMotive || loadingMotive}
          allowedPattern={onlyAlphanumericAndSpaces}
        />
      </Flex>
    </Flex>
  );
};
